import { INSIGHTS_FIELD } from "../../common/linqset-model";

// how loaded a role or person is: over its capacity, close to it (85% or more), or with room to spare
export type CapacityStatus = 'over' | 'near' | 'under';
export const NEAR_CAPACITY = 0.85;

export interface CapacityRow {
    key: string
    name: string
    hours: number           // the work it does in a year
    capacity: number        // the hours it has for it
    utilisation: number     // hours against capacity: 1 is all of it
    status: CapacityStatus
}

// each role or person with a capacity, the most loaded first; those without one can't be measured, so are counted apart
export const capacityRows = (items: { key: string, name: string, hours: number, capacity: number }[]) => {
    const rows: CapacityRow[] = items.filter(i => i.capacity > 0).map(i => {
        const utilisation = (i.hours || 0) / i.capacity;
        const status: CapacityStatus = utilisation > 1 ? 'over' : utilisation >= NEAR_CAPACITY ? 'near' : 'under';
        return { key: i.key, name: i.name, hours: i.hours || 0, capacity: i.capacity, utilisation, status };
    }).sort((a, b) => b.utilisation - a.utilisation);
    return {
        rows,
        noCapacity: items.filter(i => !(i.capacity > 0)).length,
        over: rows.filter(r => r.status === 'over').length,
        near: rows.filter(r => r.status === 'near').length,
    };
};

export interface ParetoRow {
    key: string
    name: string
    value: number
    share: number           // of the total, 0 to 1
    cumulative: number      // of the total, this and all larger, 0 to 1
    core: boolean           // among the few that make up the cut (80%) of the total
}

// the largest first, with how much of the total each brings the running total to. The core is the fewest that reach
// the cut, so "4 of 39 make up 80%". Only positive values add up to a total that means anything
export const paretoRows = (items: { key: string, name: string, value: number }[], cut = 0.8) => {
    const positive = items.filter(i => i.value > 0).sort((a, b) => b.value - a.value);
    const total = positive.reduce((t, i) => t + i.value, 0);
    let running = 0;
    const rows: ParetoRow[] = positive.map(i => {
        const before = running;
        running += i.value;
        return { key: i.key, name: i.name, value: i.value, share: i.value / total, cumulative: running / total, core: before / total < cut - 1e-9 };
    });
    return { rows, total, coreCount: rows.filter(r => r.core).length, count: items.length };
};

// what can be added up for a Pareto view: totals, not rates per item or what is left over
export const PARETO_FIELDS = [
    INSIGHTS_FIELD.ANNUAL_HOURS, INSIGHTS_FIELD.COST, INSIGHTS_FIELD.REVENUE, INSIGHTS_FIELD.VALUE, INSIGHTS_FIELD.PROCESSED, INSIGHTS_FIELD.CARBON,
    INSIGHTS_FIELD.CAPACITY, INSIGHTS_FIELD.OUTPUTS, INSIGHTS_FIELD.ACTIONS_SUPPORTED, INSIGHTS_FIELD.INFORMATION_SUPPLIED, INSIGHTS_FIELD.AUTOMATED_ACTIONS,
];

export interface HeatmapRow {
    key: string
    name: string
    cells: number[]         // one for each column, in their order
    total: number
}

// nodes against the models they work in: the largest nodes first, and the models with the most first, leaving out
// models none of them work in
export const heatmapData = (items: { key: string, name: string, byModel: Record<string, number> }[], models: { key: string, name: string }[]) => {
    const totals = new Map(models.map(m => [m.key, items.reduce((t, i) => t + Math.max(0, i.byModel[m.key] || 0), 0)]));
    const columns = models.filter(m => (totals.get(m.key) || 0) > 0).sort((a, b) => (totals.get(b.key) || 0) - (totals.get(a.key) || 0))
        .map(m => ({ key: m.key, name: m.name, total: totals.get(m.key) || 0 }));
    const rows: HeatmapRow[] = items.map(i => {
        const cells = columns.map(c => Math.max(0, i.byModel[c.key] || 0));
        return { key: i.key, name: i.name, cells, total: cells.reduce((t, v) => t + v, 0) };
    }).filter(r => r.total > 0).sort((a, b) => b.total - a.total);
    const max = Math.max(0, ...rows.flatMap(r => r.cells));
    return { rows, columns, max };
};

export interface CompareRow {
    key: string
    name: string
    before: number
    after: number
    change: number          // after less before
}

// each node before and after, the largest change (either way) first; those that don't change are counted apart
export const compareRows = (items: { key: string, name: string, before: number, after: number }[]) => {
    const all: CompareRow[] = items.map(i => ({ key: i.key, name: i.name, before: i.before || 0, after: i.after || 0, change: (i.after || 0) - (i.before || 0) }));
    const rows = all.filter(r => Math.abs(r.change) > 1e-9).sort((a, b) => Math.abs(b.change) - Math.abs(a.change) || b.after - a.after);
    const before = all.reduce((t, r) => t + r.before, 0), after = all.reduce((t, r) => t + r.after, 0);
    return {
        rows, unchanged: all.length - rows.length, before, after,
        up: rows.filter(r => r.change > 0).length, down: rows.filter(r => r.change < 0).length,
        max: Math.max(0, ...rows.flatMap(r => [r.before, r.after])),
    };
};
